import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styles from './HorizontalScrollable.module.scss.js';
import { HorizontalScrollableContext } from './HorizontalScrollableContext.js';
import messages from './intl/index.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useMessageFormatter } from '../i18n/format.js';
import { useMediaFeatures } from '../util/useMediaFeatures.js';
import { Button } from '../button/Button.js';
import { MdChevronLeft, MdChevronRight } from '../node_modules/react-icons/md/index.js';
import classNames from 'clsx';
import useResizeObserver from 'use-resize-observer';

function updateArrowVisibility(el, setShowLeftArrow, setShowRightArrow) {
  if (!el) {
    return;
  }
  setShowLeftArrow(el.scrollLeft > 0);
  setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth);
}
function scroll(viewport, direction, smoothScrolling) {
  if (!viewport) {
    return;
  }

  // Space within this scroll margin on the left and right is considered to be "offscreen" in the below algorithm.
  const scrollMargin = 64;
  let newPosition;
  if (direction === 'right') {
    // Scroll right by one full viewport by default (this could happen if only a single child element exists)
    newPosition = viewport.scrollLeft + (viewport.clientWidth - 2 * scrollMargin);

    // Scroll so that an element partially in view on the right (or the first offscreen
    // element if no elements are partially in view) ends up at the far left.
    const viewportScrollRight = viewport.scrollLeft + viewport.clientWidth;
    for (const child of viewport.children) {
      const offsetRight = child.offsetLeft + child.clientWidth;
      // Element is at least partially offscreen to the right
      if (offsetRight > viewportScrollRight - scrollMargin) {
        // Element is not already scrolled to (this could happen if the element width exceeds the viewport width)
        if (child.offsetLeft - scrollMargin > viewport.scrollLeft) {
          newPosition = child.offsetLeft - scrollMargin;
          break;
        }
      }
    }
  } else {
    // Scroll left by one full viewport by default (this could happen if only a single child element exists)
    newPosition = viewport.scrollLeft - (viewport.clientWidth - 2 * scrollMargin);

    // Scroll so that an element partially in view on the left (or the first offscreen
    // element if no elements are partially in view) ends up at the far right.
    for (const child of [...viewport.children].reverse()) {
      const offsetRight = child.offsetLeft + child.clientWidth;
      // Element is at least partially offscreen to the left
      if (child.offsetLeft < viewport.scrollLeft + scrollMargin) {
        // Element is not already scrolled to (this could happen if the element width exceeds the viewport width)
        if (offsetRight + scrollMargin - viewport.clientWidth < viewport.scrollLeft) {
          newPosition = offsetRight + scrollMargin - viewport.clientWidth;
          break;
        }
      }
    }
  }
  if (smoothScrolling) {
    viewport.scrollTo({
      left: newPosition,
      top: 0,
      behavior: 'smooth'
    });
  } else {
    viewport.scrollLeft = newPosition;
  }
}

/**
 * A horizontally scrollable region that dynamically shows paging arrows based on scroll position.
 */
function HorizontalScrollable(props) {
  const {
    children,
    className: callerClassName,
    tag,
    viewportClassName: callerViewportClassName,
    viewportRole
  } = props;
  const {
    formatMessage
  } = useMessageFormatter(messages);
  const {
    prefersReducedMotion
  } = useMediaFeatures();
  const smoothScrolling = typeof window !== 'undefined' && !prefersReducedMotion && 'scrollBehavior' in document.documentElement.style;
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const className = classNames('dsx-component', styles['wrapper'], callerClassName);
  const viewportClassName = classNames(styles['viewport'], showLeftArrow && styles['left'], showRightArrow && styles['right'], callerViewportClassName);
  const ref = useRef(null);

  // Needs to be regular effect instaed of layout effect.
  // Essentially after initial render check if arrows need to show
  // otherwise styles with transitions will flash from user-agent defaults
  // to specified styles
  useEffect(() => {
    if (ref.current !== null) {
      updateArrowVisibility(ref.current, setShowLeftArrow, setShowRightArrow);
    }
  }, [ref, setShowLeftArrow, setShowRightArrow]);
  useResizeObserver({
    ref,
    onResize: () => {
      if (!ref.current) {
        return;
      }
      updateArrowVisibility(ref.current, setShowLeftArrow, setShowRightArrow);
    }
  });
  const contextValue = useMemo(() => ({
    showLeftArrow,
    showRightArrow
  }), [showLeftArrow, showRightArrow]);
  const handleScrollLeft = useCallback(() => {
    scroll(ref.current, 'left', smoothScrolling);
  }, [ref, smoothScrolling]);
  const handleScrollRight = useCallback(() => {
    scroll(ref.current, 'right', smoothScrolling);
  }, [ref, smoothScrolling]);
  const handleViewportScroll = useCallback(e => {
    updateArrowVisibility(e.currentTarget, setShowLeftArrow, setShowRightArrow);
  }, [setShowLeftArrow, setShowRightArrow]);
  return /*#__PURE__*/jsx(HorizontalScrollableContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/jsxs("div", {
      className: className,
      "data-dsx--horizontal-scrollable": tag,
      children: [/*#__PURE__*/jsx("div", {
        className: viewportClassName,
        ref: ref,
        onScroll: handleViewportScroll,
        role: viewportRole,
        children: children
      }), showLeftArrow && /*#__PURE__*/jsx("div", {
        className: styles['arrow-left'],
        "data-dsx--horizontal-scrollable--left": tag,
        children: /*#__PURE__*/jsx(Button, {
          variant: "ui-control",
          size: "small",
          icon: MdChevronLeft,
          iconOnly: true,
          onClick: handleScrollLeft,
          children: formatMessage('scroll_left')
        })
      }), showRightArrow && /*#__PURE__*/jsx("div", {
        className: styles['arrow-right'],
        "data-dsx--horizontal-scrollable--right": tag,
        children: /*#__PURE__*/jsx(Button, {
          variant: "ui-control",
          size: "small",
          icon: MdChevronRight,
          iconOnly: true,
          onClick: handleScrollRight,
          children: formatMessage('scroll_right')
        })
      })]
    })
  });
}

export { HorizontalScrollable };
